/* eslint-disable class-methods-use-this */
import { ApolloClient, ObservableQuery } from 'apollo-client';
import { ApolloLink, Operation } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { onError } from 'apollo-link-error';
import { ServerError } from 'apollo-link-http-common';
import { ACCESS_TOKEN_KEY } from './Auth/types';
import { ErrorCodeList } from '../enums/ErrorCodeList';
import { RequestStrategy } from './Request/Strategy';
import RequestConfigurationFactory from './Request/RequestConfigurationFactory';

type UnauthorizedRequestHandler = () => Promise<void>;

const API_VERSION = 'api_version';

const uriWithOperationName = (operation: Operation): string => {
  const { operationName } = operation;
  const uri = RequestConfigurationFactory.getGraphqlUrl();
  return `${uri}?operationName=${operationName}`;
};

const generateHttpLink = () =>
  createHttpLink({
    uri: uriWithOperationName,
    credentials: 'include',
  });

/**
 * Custom static handler for signing out a user if a request responds with
 * an "Unauthenticated" http response code. This must be configured
 * by the app, to properly handle this scenario and making sure the
 * user is signed out (destroying authorization info) before the
 * service can redirect the user to the log in page
 */
let unauthorizedRequestHandler: UnauthorizedRequestHandler;

/**
 * Static instance for an http link that may vary depending on what the
 * value is for the API URL, obtained from the configuration bootstrap.
 * The very first initial request is always made to the API URL that comes
 * from a default env variable, from there, the real url is obtained, which
 * is then used for every subsequent request
 */
let httpLink = generateHttpLink();

// @see https://www.apollographql.com/docs/react/api/link/introduction/
const afterwareLink = new ApolloLink((operation, forward) =>
  forward(operation).map((response) => {
    const context = operation.getContext();
    const { headers } = context.response;

    if (!headers) {
      return response;
    }

    const localApiVersion = localStorage.getItem(API_VERSION);
    const apiVersion = headers.get(API_VERSION);
    if (
      (!localApiVersion && apiVersion && apiVersion !== 'null') ||
      (localApiVersion !== apiVersion && apiVersion !== 'null')
    ) {
      localStorage.setItem(API_VERSION, apiVersion);
    }

    return response;
  })
);

/**
 * Apollo link to catch network errors. Specifically, error 401 which means
 * that there has been some error regarding the current session. If that's
 * the case, the user is signed out and redirected to the login page
 * with a custom message that they should sign in again.
 * @see https://www.apollographql.com/docs/react/v2/data/error-handling/
 */
const errorLink = onError(({ networkError }) => {
  if (!networkError) {
    return;
  }

  try {
    if (!Object.prototype.hasOwnProperty.call(networkError, 'response')) {
      return;
    }

    if (
      (networkError as ServerError)?.response?.status ===
      ErrorCodeList.Unauthenticated
    ) {
      /**
       * "Signing out" the user by cleaning the authorization items in
       * local storage or cleaning cookies, anonymizing tools and notifying
       * the extension prior to redirecting the user to the login screen
       */
      unauthorizedRequestHandler?.();
    }
  } catch (error) {
    // logging this error which would mean the network error couldn't be casted
    console.log(error);
  }
});

// Reload multiple tabs when the API version changes
window.addEventListener('storage', ({ key, newValue }) => {
  if (key === API_VERSION) window.location.reload();
  // SSO log out
  if (key === ACCESS_TOKEN_KEY && !newValue) window.location.reload();
});

export default class ApolloService {
  private requestStrategy: RequestStrategy;

  constructor(strategy: RequestStrategy) {
    this.requestStrategy = strategy;
  }

  /**
   * Setting the custom handler for signing out a user when a request
   * responds with a 401 ("Unauthenticated") http code
   */
  public static setUnauthorizedRequestHandler(
    handler: UnauthorizedRequestHandler
  ) {
    unauthorizedRequestHandler = handler;
  }

  /**
   * Updates the httpLink instance with a new one that will have an updated
   * API URL. This is necessary as the URL may vary depending on the data center
   * or region the user is located
   */
  public static refreshApiUrl() {
    httpLink = generateHttpLink();
  }

  public createApolloService() {
    return new ApolloClient({
      link: errorLink.concat(afterwareLink.concat(httpLink)),
      cache: new InMemoryCache({
        // @warning
        // __typename is an internal apollo cache flag
        // having it creates conflicts when loading and then mutating the data
        // ie. load content attributes and then edit them
        addTypename: false,
      }),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
        query: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
        mutate: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
      },
    });
  }

  public executeQuery<TData = any, TVariables = any>(
    params: any
  ): ObservableQuery<TData, TVariables> {
    return this.createApolloService().watchQuery<TData, TVariables>({
      context: {
        headers: this.requestStrategy.getRequestHeaders(),
      },
      query: params.query,
      variables: params.variables,
    });
  }

  public executeMutation(params: any) {
    return this.createApolloService().mutate({
      context: {
        headers: this.requestStrategy.getRequestHeaders(),
      },
      mutation: params.mutation,
      variables: params.variables,
    });
  }
}
